import {inlineError} from '@s-ui/decorators'

import {FormRepository} from './FormRepository.js'

export class HTTPFormRepository extends FormRepository {
  /**
   * @constructor
   * @param {Object}    deps
   * @param {Config}    deps.config
   * @param {Fetcher}   deps.fetchOrFail
   * @param {Function}  deps.captchaStatusGenericFormError
   * @param {Function}  deps.captchaStatusNotActiveFormError
   * @param {Function}  deps.captchaStatusNotFoundFormError
   * @param {Function}  deps.configurationNotFoundFormError
   * @param {Function}  deps.fromModelDetailFormConfigResponseToModelDetailFormConfigVOMapper
   * @param {Function}  deps.genericFormError
   * @param {Function}  deps.log
   * @param {Function}  deps.specsFormError
   * @param {Function}  deps.specsFormValueObject
   */
  constructor({
    captchaStatusGenericFormError,
    captchaStatusNotActiveFormError,
    captchaStatusNotFoundFormError,
    config,
    configurationNotFoundFormError,
    fetchOrFail,
    fromCaptchaStatusResponseToCaptchaFormValueObjectMapper,
    fromModelDetailFormConfigResponseToModelDetailFormConfigVOMapper,
    fromSpecsFormListToSpecsDatalistValueObjectMapper,
    genericFormError,
    specsFormError,
    specsFormValueObject
  }) {
    super()

    this._config = config
    this._fetchOrFail = fetchOrFail
    this._captchaStatusGenericFormError = captchaStatusGenericFormError
    this._captchaStatusNotActiveFormError = captchaStatusNotActiveFormError
    this._captchaStatusNotFoundFormError = captchaStatusNotFoundFormError
    this._configurationNotFoundFormError = configurationNotFoundFormError
    this._fromCaptchaStatusResponseToCaptchaFormValueObjectMapper =
      fromCaptchaStatusResponseToCaptchaFormValueObjectMapper
    this._fromModelDetailFormConfigResponseToModelDetailFormConfigVOMapper =
      fromModelDetailFormConfigResponseToModelDetailFormConfigVOMapper
    this._fromSpecsFormListToSpecsDatalistValueObjectMapper = fromSpecsFormListToSpecsDatalistValueObjectMapper
    this._genericFormError = genericFormError
    this._specsFormValueObject = specsFormValueObject
    this._specsFormError = specsFormError

    this._getCaptchaRequestHeaders = ({pageName, context}) => {
      const {CAPTCHA_ORIGIN} = this._config.get('REQUEST_HEADERS')
      const {CAPTCHA_ORIGINS_BY_PAGENAME, CAPTCHA_ORIGINS_BY_CONTEXT} = this._config.get('page')
      return {
        [CAPTCHA_ORIGIN]: CAPTCHA_ORIGINS_BY_CONTEXT[context] || CAPTCHA_ORIGINS_BY_PAGENAME[pageName]
      }
    }
  }

  @inlineError
  async getSpecsForm({searchUrlVO}) {
    const {url} = searchUrlVO.toJSON()
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: this._specsFormError,
        [GENERIC]: this._specsFormError
      })
      .get(url)

    if (error) return Promise.reject(error)

    if (response.data?.items) {
      const datalist = this._fromSpecsFormListToSpecsDatalistValueObjectMapper.map({
        items: response.data.items
      })

      return this._specsFormValueObject({
        ...datalist.toJSON(),
        disabled: false,
        hidden: false
      })
    }

    return this._specsFormValueObject(response.data)
  }

  @inlineError
  async getCaptchaStatus({pageName, context}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS

    const headers = this._getCaptchaRequestHeaders({pageName, context})
    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: this._captchaStatusNotFoundFormError,
        [GENERIC]: this._captchaStatusGenericFormError
      })
      .get(`${this._config.get('API_URL')}/captcha/status`, {headers})

    if (error) return Promise.reject(error)

    const {data} = response

    const {active} = data
    if (!active) return Promise.reject(this._captchaStatusNotActiveFormError())

    return this._fromCaptchaStatusResponseToCaptchaFormValueObjectMapper.map(data)
  }

  @inlineError
  async getModelDetailFormConfig({makeId}) {
    const {NOT_FOUND} = this._config.get('COMMON').HTTP_STATUS_CODES
    const {GENERIC} = this._config.get('COMMON').ERRORS
    const APP_NAME = this._config.get('APP_NAME')
    const APP_NAME_MOTOS = this._config.get('APP_NAME_MOTOS')

    if (APP_NAME === APP_NAME_MOTOS) {
      return Promise.reject(
        this._genericFormError('[HTTPFormRepository#getModelDetailFormConfig] not implemented for motos')
      )
    }

    const url = `${this._config.get('API_URL')}/vehicle/modeldetail/makeleadconfiguration/${makeId}`

    const [error, response] = await this._fetchOrFail
      .withErrorsFactories({
        [NOT_FOUND]: this._configurationNotFoundFormError,
        [GENERIC]: this._genericFormError
      })
      .get(url)

    if (error) return Promise.reject(error)

    const {data} = response

    return this._fromModelDetailFormConfigResponseToModelDetailFormConfigVOMapper.map(data)
  }
}
